import { z } from "zod";
import { getIDToken } from "utils/misc";
import { QuestionSchema } from "./types/common";
import * as persisted from "./types/persisted";
import * as resolved from "./types/resolved";

export type PredicateErrorDetail = { field: string; message: string };
export type SuggestedDependency = { question_id: number; feel_id: string };

export type PredicateValidationError = {
  kind: "predicateValidation";
  message: string;
  details: PredicateErrorDetail[];
  suggested_dependencies: SuggestedDependency[];
};

export const isPredicateValidationError = (err: unknown): err is PredicateValidationError =>
  typeof err === "object" && err !== null && (err as PredicateValidationError).kind === "predicateValidation";

const SuggestedDependencySchema = z.object({
  question_id: z.number(),
  feel_id: z.string(),
});

const PredicateErrorResponseSchema = z.object({
  error: z.string().refine(s => s.startsWith("invalid predicate")),
  details: z.array(z.object({ field: z.string(), message: z.string() })),
  suggested_dependencies: z.array(SuggestedDependencySchema).optional(),
});

const throwIfPredicateError = async (res: Response) => {
  if (res.status === 400) {
    const body = await res.json();
    const parsed = PredicateErrorResponseSchema.safeParse(body);
    if (parsed.success) {
      const err: PredicateValidationError = {
        kind: "predicateValidation",
        message: "invalid predicate",
        details: parsed.data.details,
        suggested_dependencies: parsed.data.suggested_dependencies ?? [],
      };
      throw err;
    }
  }
};

const PatientContextSchema = z.object({
  joint: z.string(),
  ailment: z.string(),
  market: z.string(),
  locale: z.string(),
  medication: z
    .array(z.string())
    .nullish()
    .transform(v => v ?? []),
  medication_brand: z
    .array(z.string())
    .nullish()
    .transform(v => v ?? []),
});

const PatientPreviewResponseSchema = z.object({
  patient_context: PatientContextSchema,
  questionnaire: resolved.QuestionnaireSchema,
});

const QuestionPreviewResponseSchema = z.object({
  patient_context: PatientContextSchema,
  question: resolved.ResolvedQuestionSchema,
});

const QuestionGroupPreviewResponseSchema = z.object({
  patient_context: PatientContextSchema,
  question_group: resolved.QuestionGroupSchema,
});

const DiffResolvedResponseSchema = z.object({
  questionnaire: resolved.QuestionnaireSchema,
});

export type PatientContext = z.infer<typeof PatientContextSchema>;
export type ResolvedQuestion = resolved.ResolvedQuestion;
export type ResolvedQuestionGroup = resolved.QuestionGroup;
export type ResolvedQuestionnaire = resolved.Questionnaire;
export type PatientPreviewResponse = z.infer<typeof PatientPreviewResponseSchema>;
export type DiffResolvedResponse = z.infer<typeof DiffResolvedResponseSchema>;
export type QuestionPreviewResponse = z.infer<typeof QuestionPreviewResponseSchema>;
export type QuestionGroupPreviewResponse = z.infer<typeof QuestionGroupPreviewResponseSchema>;

type Question = z.infer<typeof QuestionSchema>;

const BASE_URL = `${process.env.REACT_APP_API_URL_V2}/v2/support/health_forms`;
const SUBSTANCES_URL = `${process.env.REACT_APP_API_URL_V2}/v2/support/medication-substances`;
const MEDICATIONS_URL = `${process.env.REACT_APP_API_URL_V2}/v2/support/medications`;

const CreatedResponseSchema = z.object({ id: z.number() });

const getHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  const token = getIDToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

const QuestionListItemSchema = z.object({
  id: z.number(),
  question: z.string().optional(),
  semantic_key: z.string().optional().nullable(),
});

type QuestionListItem = z.infer<typeof QuestionListItemSchema>;

const QuestionnaireListItemSchema = z.object({
  id: z.number(),
  name: z.string().optional(),
  has_unpublished_changes: z.boolean(),
});

type QuestionnaireListItem = z.infer<typeof QuestionnaireListItemSchema>;

const QuestionGroupListItemSchema = z.object({
  id: z.number(),
  name: z.string().optional(),
});

type QuestionGroupListItem = z.infer<typeof QuestionGroupListItemSchema>;

const UsageItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.enum(["questionnaire", "questionGroup"]),
  is_direct: z.boolean(),
  page_position: z.number().optional(),
});

type UsageItem = z.infer<typeof UsageItemSchema>;

type QuestionListParams = {
  search?: string;
  type?: string;
  usage?: string;
};

type SearchParams = {
  search?: string;
};

const RolloutStatusSchema = z.enum([
  "active",
  "activating",
  "stalled",
  "unpublishing",
  "retiring",
  "retired",
  "inactive",
]);

const QuestionnaireRolloutSchema = z.object({
  group: z.string(),
  status: RolloutStatusSchema,
  target_published_questionnaire_id: z.number().optional(),
  target_published_at: z.string().optional(),
  deployed_by_email: z.string().nullish(),
  deployed_at: z.string().nullish(),
});

const RevisionRolloutSchema = z.object({
  group: z.string(),
  status: RolloutStatusSchema,
  confirmed_at: z.string().optional(),
  deployed_by_email: z.string().nullish(),
  deployed_at: z.string().nullish(),
});

const PublishedQuestionnaireListItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  source_questionnaire_id: z.number().nullable(),
  published_at: z.string(),
  published_by: z.string(),
  is_active: z.boolean(),
  rollout: z.array(QuestionnaireRolloutSchema),
});

type PublishedQuestionnaireListItem = z.infer<typeof PublishedQuestionnaireListItemSchema>;

const PublishedQuestionnaireDetailSchema = persisted.ExpandedQuestionnaireSchema.extend({
  source_questionnaire_id: z.number().nullable(),
  published_at: z.string(),
  published_by: z.string(),
});

type PublishedQuestionnaireDetail = z.infer<typeof PublishedQuestionnaireDetailSchema>;

const PublishCheckConflictSchema = z.object({
  name: z.string(),
  active_published_id: z.number(),
  active_published_at: z.string(),
  active_published_by: z.string(),
});

type PublishCheckConflict = z.infer<typeof PublishCheckConflictSchema>;

const RestorePreviewEntitySchema = z.object({
  kind: z.enum(["questionnaire", "question", "question_group"]),
  source_id: z.number(),
  label: z.string(),
  status: z.enum(["modified", "missing"]),
  used_by: z.array(UsageItemSchema),
});

const RestorePreviewSchema = z.object({
  entities: z.array(RestorePreviewEntitySchema),
});

type RestorePreviewEntity = z.infer<typeof RestorePreviewEntitySchema>;
type RestorePreview = z.infer<typeof RestorePreviewSchema>;

const PublishedQuestionnaireHistoryItemSchema = z.object({
  id: z.number(),
  published_at: z.string(),
  published_by: z.string(),
  is_active: z.boolean(),
  rollout: z.array(RevisionRolloutSchema),
});

type PublishedQuestionnaireHistoryItem = z.infer<typeof PublishedQuestionnaireHistoryItemSchema>;

type RolloutStatus = z.infer<typeof RolloutStatusSchema>;
type QuestionnaireRollout = z.infer<typeof QuestionnaireRolloutSchema>;
type RevisionRollout = z.infer<typeof RevisionRolloutSchema>;

export const healthFormsApi = {
  questions: {
    list: async (params?: QuestionListParams): Promise<QuestionListItem[]> => {
      const url = new URL(`${BASE_URL}/questions`);
      if (params?.search) url.searchParams.set("search", params.search);
      if (params?.type) url.searchParams.set("type", params.type);
      if (params?.usage) url.searchParams.set("usage", params.usage);
      const res = await fetch(url.toString(), {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch questions");
      const data = await res.json();
      return z.array(QuestionListItemSchema).parse(data);
    },

    get: async (id: number) => {
      const res = await fetch(`${BASE_URL}/questions/${id}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch question ${id}`);
      const data = await res.json();
      return QuestionSchema.parse(data);
    },

    create: async () => {
      const res = await fetch(`${BASE_URL}/questions`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ _type: "question" }),
      });
      if (!res.ok) throw new Error("Failed to create question");
      return CreatedResponseSchema.parse(await res.json());
    },

    update: async (id: number, question: Question): Promise<Question> => {
      const res = await fetch(`${BASE_URL}/questions/${id}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(question),
      });
      if (!res.ok) throw new Error(`Failed to update question ${id}`);
      if (res.status === 204) return question;
      return res.json();
    },

    delete: async (id: number): Promise<void> => {
      const res = await fetch(`${BASE_URL}/questions/${id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to delete question ${id}`);
    },

    usage: async (id: number): Promise<UsageItem[]> => {
      const res = await fetch(`${BASE_URL}/questions/${id}/usage`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch question ${id} usage`);
      const data = await res.json();
      return z.array(UsageItemSchema).parse(data);
    },

    preview: async (id: number, attrs: PreviewAttrs): Promise<QuestionPreviewResponse> => {
      const res = await fetch(`${BASE_URL}/questions/${id}/preview?${attrsToQuery(attrs)}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch question ${id} preview`);
      const data = await res.json();
      return QuestionPreviewResponseSchema.parse(data);
    },
  },

  questionnaires: {
    list: async (params?: SearchParams): Promise<QuestionnaireListItem[]> => {
      const url = new URL(`${BASE_URL}/questionnaires`);
      if (params?.search) url.searchParams.set("search", params.search);
      const res = await fetch(url.toString(), {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch questionnaires");
      const data = await res.json();
      return z.array(QuestionnaireListItemSchema).parse(data);
    },

    get: async (id: number) => {
      const res = await fetch(`${BASE_URL}/questionnaires/${id}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch questionnaire ${id}`);
      const data = await res.json();
      return persisted.ExpandedQuestionnaireSchema.parse(data);
    },

    getByName: async (name: string): Promise<QuestionnaireListItem | null> => {
      const res = await fetch(`${BASE_URL}/questionnaires/by-name/${encodeURIComponent(name)}`, {
        headers: getHeaders(),
      });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`Failed to fetch questionnaire by name "${name}"`);
      const data = await res.json();
      return QuestionnaireListItemSchema.parse(data);
    },

    create: async (input?: { name?: string; pages?: persisted.Questionnaire["pages"] }) => {
      const res = await fetch(`${BASE_URL}/questionnaires`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ _type: "questionnaire", ...input }),
      });
      if (!res.ok) throw new Error("Failed to create questionnaire");
      return CreatedResponseSchema.parse(await res.json());
    },

    update: async (id: number, questionnaire: persisted.Questionnaire): Promise<persisted.Questionnaire> => {
      const res = await fetch(`${BASE_URL}/questionnaires/${id}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(questionnaire),
      });
      if (!res.ok) {
        await throwIfPredicateError(res);
        throw new Error(`Failed to update questionnaire ${id}`);
      }
      if (res.status === 204) return questionnaire;
      return res.json();
    },

    delete: async (id: number): Promise<void> => {
      const res = await fetch(`${BASE_URL}/questionnaires/${id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to delete questionnaire ${id}`);
    },

    publishCheck: async (id: number): Promise<PublishCheckConflict | null> => {
      const res = await fetch(`${BASE_URL}/questionnaires/${id}/publish_check`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to check publish status for questionnaire ${id}`);
      const data = await res.json();
      if (data === null) return null;
      return PublishCheckConflictSchema.parse(data);
    },

    publish: async (id: number) => {
      const res = await fetch(`${BASE_URL}/questionnaires/${id}/publish`, {
        method: "POST",
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to publish questionnaire ${id}`);
      return CreatedResponseSchema.parse(await res.json());
    },

    preview: async (id: number, attrs: PreviewAttrs): Promise<PatientPreviewResponse> => {
      const res = await fetch(`${BASE_URL}/questionnaires/${id}/preview?${attrsToQuery(attrs)}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch questionnaire ${id} preview`);
      const data = await res.json();
      return PatientPreviewResponseSchema.parse(data);
    },

    diffResolved: async (id: number, locale: PreviewLocale): Promise<DiffResolvedResponse> => {
      const res = await fetch(`${BASE_URL}/questionnaires/${id}/diff_resolved?locale=${locale}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch questionnaire ${id} diff resolution`);
      const data = await res.json();
      return DiffResolvedResponseSchema.parse(data);
    },

    restorePreview: async (id: number, publishedQuestionnaireId: number): Promise<RestorePreview> => {
      const res = await fetch(
        `${BASE_URL}/questionnaires/${id}/restore_preview?published_questionnaire_id=${publishedQuestionnaireId}`,
        { headers: getHeaders() }
      );
      if (!res.ok) throw new Error(`Failed to fetch restore preview for questionnaire ${id}`);
      const data = await res.json();
      return RestorePreviewSchema.parse(data);
    },

    restore: async (id: number, publishedQuestionnaireId: number): Promise<void> => {
      const res = await fetch(`${BASE_URL}/questionnaires/${id}/restore`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ published_questionnaire_id: publishedQuestionnaireId }),
      });
      if (!res.ok) throw new Error(`Failed to restore questionnaire ${id}`);
    },
  },

  publishedQuestionnaires: {
    list: async (params?: SearchParams): Promise<PublishedQuestionnaireListItem[]> => {
      const url = new URL(`${BASE_URL}/published_questionnaires`);
      if (params?.search) url.searchParams.set("search", params.search);
      const res = await fetch(url.toString(), {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch published questionnaires");
      const data = await res.json();
      return z.array(PublishedQuestionnaireListItemSchema).parse(data);
    },

    get: async (id: number): Promise<PublishedQuestionnaireDetail> => {
      const res = await fetch(`${BASE_URL}/published_questionnaires/${id}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch published questionnaire ${id}`);
      const data = await res.json();
      return PublishedQuestionnaireDetailSchema.parse(data);
    },

    history: async (name: string): Promise<PublishedQuestionnaireHistoryItem[]> => {
      const url = new URL(`${BASE_URL}/published_questionnaires/history`);
      url.searchParams.set("name", name);
      const res = await fetch(url.toString(), {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch history for published questionnaire "${name}"`);
      const data = await res.json();
      return z.array(PublishedQuestionnaireHistoryItemSchema).parse(data);
    },

    preview: async (id: number, attrs: PreviewAttrs): Promise<PatientPreviewResponse> => {
      const res = await fetch(`${BASE_URL}/published_questionnaires/${id}/preview?${attrsToQuery(attrs)}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch published questionnaire ${id} preview`);
      const data = await res.json();
      return PatientPreviewResponseSchema.parse(data);
    },

    diffResolved: async (id: number, locale: PreviewLocale): Promise<DiffResolvedResponse> => {
      const res = await fetch(`${BASE_URL}/published_questionnaires/${id}/diff_resolved?locale=${locale}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch published questionnaire ${id} diff resolution`);
      const data = await res.json();
      return DiffResolvedResponseSchema.parse(data);
    },
  },

  questionGroups: {
    list: async (params?: SearchParams): Promise<QuestionGroupListItem[]> => {
      const url = new URL(`${BASE_URL}/question_groups`);
      if (params?.search) url.searchParams.set("search", params.search);
      const res = await fetch(url.toString(), {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch question groups");
      const data = await res.json();
      return z.array(QuestionGroupListItemSchema).parse(data);
    },

    get: async (id: number) => {
      const res = await fetch(`${BASE_URL}/question_groups/${id}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch question group ${id}`);
      const data = await res.json();
      return persisted.ExpandedQuestionGroupSchema.parse(data);
    },

    create: async (input?: Omit<persisted.QuestionGroup, "_type" | "id" | "updated_at" | "edited_by">) => {
      const res = await fetch(`${BASE_URL}/question_groups`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ _type: "question_group", ...input }),
      });
      if (!res.ok) throw new Error("Failed to create question group");
      return CreatedResponseSchema.parse(await res.json());
    },

    update: async (id: number, group: persisted.QuestionGroup): Promise<persisted.QuestionGroup> => {
      const res = await fetch(`${BASE_URL}/question_groups/${id}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(group),
      });
      if (!res.ok) {
        await throwIfPredicateError(res);
        throw new Error(`Failed to update question group ${id}`);
      }
      if (res.status === 204) return group;
      return res.json();
    },

    delete: async (id: number): Promise<void> => {
      const res = await fetch(`${BASE_URL}/question_groups/${id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to delete question group ${id}`);
    },

    usage: async (id: number): Promise<UsageItem[]> => {
      const res = await fetch(`${BASE_URL}/question_groups/${id}/usage`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch question group ${id} usage`);
      const data = await res.json();
      return z.array(UsageItemSchema).parse(data);
    },

    preview: async (id: number, attrs: PreviewAttrs): Promise<QuestionGroupPreviewResponse> => {
      const res = await fetch(`${BASE_URL}/question_groups/${id}/preview?${attrsToQuery(attrs)}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch question group ${id} preview`);
      const data = await res.json();
      return QuestionGroupPreviewResponseSchema.parse(data);
    },
  },

  rollout: {
    publishToGroup: async (publishedQuestionnaireId: number, group: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/published_questionnaires/${publishedQuestionnaireId}/publish_to_group`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ group }),
      });
      if (!res.ok) throw new Error(`Failed to publish to ${group}`);
    },
  },

  medicationSubstances: {
    list: async (): Promise<MedicationSubstance[]> => {
      const res = await fetch(SUBSTANCES_URL, { headers: getHeaders() });
      if (!res.ok) throw new Error("Failed to fetch medication substances");
      const data = await res.json();
      return z
        .array(z.object({ id: z.number(), semantic_key: z.string() }))
        .parse(data)
        .map(s => ({ id: s.id, key: s.semantic_key, label: s.semantic_key.replace(/_/g, " ") }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
  },

  medicationBrands: {
    list: async (): Promise<MedicationBrand[]> => {
      const res = await fetch(MEDICATIONS_URL, { headers: getHeaders() });
      if (!res.ok) throw new Error("Failed to fetch medications");
      const data = await res.json();
      return z
        .array(
          z.object({
            semantic_key: z.string().nullish(),
            substance_id: z.number().nullish(),
            translations: z.array(z.object({ name: z.string() })).catch([]),
          })
        )
        .parse(data)
        .flatMap(m =>
          m.semantic_key
            ? [
                {
                  key: m.semantic_key,
                  substanceId: m.substance_id ?? null,
                  label: m.translations[0]?.name ?? m.semantic_key.replace(/_/g, " "),
                },
              ]
            : []
        )
        .sort((a, b) => a.label.localeCompare(b.label));
    },
  },
};

export type MedicationSubstance = { id: number; key: string; label: string };

export type MedicationBrand = { key: string; substanceId: number | null; label: string };

export const JOINTS = [
  "knee",
  "hip",
  "foot",
  "hand",
  "elbow",
  "shoulder",
  "jaw",
  "neck",
  "spine",
  "lower_back",
  "upper_back",
  "wrist",
  "lower_leg",
  "other",
] as const;

export const AILMENTS = ["joint_pain", "fall_prevention", "osteoporosis", "other_ailment"] as const;

export const MARKETS = ["se", "gb", "us"] as const;

export const LOCALES = ["sv-SE", "en-US", "en-GB"] as const;

export const PREMIUM_TYPES = ["none", "selfcare", "guided_care"] as const;

export const SUBSCRIPTION_PRODUCTS = [
  "treatment",
  "premium_selfcare",
  "premium_guided_care",
  "pt_on_demand",
  "glp_weight_management",
  "out_of_pocket",
] as const;

const PreviewAttrsCommon = {
  market: z.enum(MARKETS),
  locale: z.enum(LOCALES),
  premium: z.enum(PREMIUM_TYPES).catch("none"),
  medication: z.array(z.string()).catch([]),
  medicationBrand: z.array(z.string()).catch([]),
  subscription: z.array(z.enum(SUBSCRIPTION_PRODUCTS)).catch([]),
  canMessageTherapist: z.boolean().catch(true),
};

export const PreviewAttrsSchema = z.discriminatedUnion("ailment", [
  z.object({ ailment: z.literal("joint_pain"), joint: z.enum(JOINTS), ...PreviewAttrsCommon }),
  z.object({ ailment: z.literal("fall_prevention"), ...PreviewAttrsCommon }),
  z.object({ ailment: z.literal("osteoporosis"), ...PreviewAttrsCommon }),
  z.object({ ailment: z.literal("other_ailment"), ...PreviewAttrsCommon }),
]);

export type Joint = (typeof JOINTS)[number];
export type Ailment = (typeof AILMENTS)[number];
export type Market = (typeof MARKETS)[number];
export type PreviewLocale = (typeof LOCALES)[number];
export type PremiumType = (typeof PREMIUM_TYPES)[number];
export type SubscriptionProduct = (typeof SUBSCRIPTION_PRODUCTS)[number];

export type PreviewAttrs = z.infer<typeof PreviewAttrsSchema>;

export const DEFAULT_PREVIEW_ATTRS: PreviewAttrs = {
  joint: "knee",
  ailment: "joint_pain",
  market: "se",
  locale: "sv-SE",
  premium: "none",
  medication: [],
  medicationBrand: [],
  subscription: [],
  canMessageTherapist: true,
};

const attrsToQuery = (attrs: PreviewAttrs) => {
  const params = new URLSearchParams({
    ailment: attrs.ailment,
    market: attrs.market,
    locale: attrs.locale,
    premium: attrs.premium,
    can_message_therapist: String(attrs.canMessageTherapist),
  });
  if (attrs.ailment === "joint_pain") params.set("joint", attrs.joint);
  attrs.medication.forEach(m => params.append("medication", m));
  attrs.medicationBrand.forEach(m => params.append("medication_brand", m));
  attrs.subscription.forEach(s => params.append("subscription", s));
  return params.toString();
};

export type {
  QuestionListItem,
  QuestionListParams,
  SearchParams,
  QuestionnaireListItem,
  QuestionGroupListItem,
  UsageItem,
  PublishedQuestionnaireListItem,
  PublishedQuestionnaireDetail,
  PublishCheckConflict,
  PublishedQuestionnaireHistoryItem,
  RestorePreview,
  RestorePreviewEntity,
  RolloutStatus,
  QuestionnaireRollout,
  RevisionRollout,
};
